import { RegisterScreen } from '@/components/RegisterScreen';
import { formatNumber } from '@/components/live';
import { plotCell, plotField, usePracticePickers } from '@/screens/parts/practiceFields';
import {
  IRRIGATION_FREQUENCIES, IRRIGATION_METHODS, IRRIGATION_SOURCES, SEASONS, optionLabel,
} from '@/lib/practiceLists';

interface Row {
  id: string; irrigation_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  source_type: string; irrigation_method: string | null; frequency: string; season: string;
  hours_per_irrigation: number | null; shared_source: boolean; water_permit_ref: string | null;
}

/**
 * Where every plot gets its water (owner, 2026-09-25: "these all make as ui pages under Plot
 * management").
 *
 * Unlike the other five this is not a diary - a plot's water sources change rarely, and a plot
 * with none recorded has not been asked rather than not been irrigated. Read across plots it
 * answers the two questions that are about the landscape and not the farm: who is drawing
 * groundwater without a permit on file, and which sources are shared with people who are not in
 * this register at all.
 */
export default function IrrigationScreen() {
  const pick = usePracticePickers();
  // A licence is asked for where the water is taken from a body somebody else also depends on.
  const needsPermit = (row: Row) => ['borewell', 'open_well', 'canal', 'river_stream', 'pond_tank', 'check_dam'].includes(row.source_type);

  return (
    <RegisterScreen<Row>
      plotFilter
      addLabel="Add source" exportName="irrigation-sources"
      orderBy={{ column: 'created_at', ascending: false }}
      recordLabel={(row) => `irrigation source ${optionLabel(IRRIGATION_SOURCES, row.source_type)} on ${row.plot_code ?? ''}`}
      screenKey="plot.mapping"
      searchColumns={['irrigation_code', 'plot_code', 'farmer_name', 'water_permit_ref']}
      subtitle="Each plot's water sources, how often they are used, and whether a permit is on file."
      table="ops.plot_irrigation_sources" title="Irrigation" view="plot_irrigation_sources"
      columns={[
        { key: 'plot_code', label: 'Plot', render: plotCell },
        { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
        { key: 'source_type', label: 'Source', render: (row) => (
          <span className="cell-stack">
            <strong>{optionLabel(IRRIGATION_SOURCES, row.source_type)}</strong>
            <small className="muted">{row.irrigation_method ? optionLabel(IRRIGATION_METHODS, row.irrigation_method) : ''}</small>
          </span>
        ) },
        { key: 'frequency', label: 'Frequency', render: (row) => optionLabel(IRRIGATION_FREQUENCIES, row.frequency) },
        { key: 'season', label: 'Season', render: (row) => optionLabel(SEASONS, row.season) },
        { key: 'hours_per_irrigation', label: 'Hours each', align: 'right',
          render: (row) => (row.hours_per_irrigation == null ? '--' : formatNumber(row.hours_per_irrigation, 1)) },
        { key: 'shared_source', label: 'Shared', render: (row) => (row.shared_source ? 'Shared' : <span className="muted">--</span>) },
        { key: 'water_permit_ref', label: 'Permit', render: (row) => (
          row.water_permit_ref ? row.water_permit_ref
            : needsPermit(row) ? <span className="field-error">none on file</span> : <span className="muted">--</span>
        ) },
      ]}
      stats={[
        { key: 'rainfed', label: 'Rainfed', tone: 'neutral', match: (row) => row.source_type === 'rainfed' },
        { key: 'no_permit', label: 'Abstraction without a permit', tone: 'warn',
          match: (row) => needsPermit(row) && !row.water_permit_ref },
        { key: 'shared', label: 'Shared source', tone: 'info', match: (row) => row.shared_source },
        { key: 'drip', label: 'Drip or sprinkler', tone: 'ok',
          match: (row) => row.irrigation_method === 'drip' || row.irrigation_method === 'sprinkler' },
      ]}
      defaults={{ season: 'all_year' }}
      fields={[
        plotField(pick.plots),
        { key: 'source_type', label: 'Source', type: 'select', options: IRRIGATION_SOURCES, required: true },
        { key: 'irrigation_method', label: 'Method', type: 'select', options: IRRIGATION_METHODS },
        { key: 'frequency', label: 'Frequency', type: 'select', options: IRRIGATION_FREQUENCIES, required: true },
        { key: 'season', label: 'Season', type: 'select', options: SEASONS, required: true },
        { key: 'hours_per_irrigation', label: 'Hours per irrigation', type: 'number', showIf: (form) => form.source_type !== 'rainfed' },
        { key: 'shared_source', label: 'Shared with others', type: 'check', placeholder: 'Source is shared', showIf: (form) => form.source_type !== 'rainfed' },
        { key: 'water_permit_ref', label: 'Water permit / licence ref.', type: 'text', placeholder: 'e.g. SWID/2025/0412', showIf: (form) => form.source_type !== 'rainfed' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => ({
        frequency: form.source_type === 'rainfed' && !['none', 'seasonal', 'as_needed', ''].includes(String(form.frequency))
          ? 'A rainfed plot: choose None, Seasonal or As needed.' : undefined,
      })}
    />
  );
}
